/**
 * Utility functions for the Errigal Events Manager app
 */

/**
 * Format a number as EUR currency
 * @param {number} n - The number to format
 * @returns {string} Formatted currency string (e.g., "€1,234.56")
 */
export const fmt = (n) => {
  if (n === null || n === undefined) return '€0.00';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
  }).format(n);
};

/**
 * Parse a value into a LOCAL-midnight Date.
 * For "yyyy-MM-dd" strings we construct the Date using local-time components
 * (not UTC) so "2026-05-22" is always Friday 22 May in Irish time, regardless
 * of DST or the browser's timezone.
 */
const _toLocalDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d === 'string') {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

/**
 * Format a date as short string
 * @param {Date|string} d - The date to format
 * @returns {string} Short date format (e.g., "Wed, 8 Apr")
 */
export const fmtDate = (d) => {
  const date = _toLocalDate(d);
  if (!date) return '';
  return date.toLocaleDateString('en-IE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

/**
 * Format a date as long string
 * @param {Date|string} d - The date to format
 * @returns {string} Long date format (e.g., "Wednesday 8th April 2026")
 */
export const fmtDateLong = (d) => {
  const date = _toLocalDate(d);
  if (!date) return '';

  const dayNum = date.getDate();
  const dayName = date.toLocaleDateString('en-IE', { weekday: 'long' });
  const monthYear = date.toLocaleDateString('en-IE', { month: 'long', year: 'numeric' });

  // Add ordinal suffix
  const suffix = ['st', 'nd', 'rd'][dayNum % 10 - 1] || 'th';
  if (dayNum > 20 && dayNum < 24) return `${dayName} ${dayNum}th ${monthYear}`;

  return `${dayName} ${dayNum}${suffix} ${monthYear}`;
};

/**
 * Get a venue by ID from venues array
 * @param {string} id - The venue ID
 * @param {Array} venues - Array of venue objects
 * @returns {Object|null} The venue object or null if not found
 */
export const getVenue = (id, venues) => {
  if (!venues || !id) return null;
  return venues.find((v) => v.id === id) || null;
};

/**
 * Get the Monday of the week containing the given date
 * @param {Date|string} date - The date
 * @returns {Date} Monday of that week
 */
export const getMonday = (date) => {
  const parsed = _toLocalDate(date) || new Date();
  // Clone so we don't mutate the caller's Date
  const d = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  d.setDate(diff);
  return d;
};

/**
 * Get the Monday and Sunday dates for a week
 * @param {Date|string} date - Any date in the week
 * @returns {Object} Object with monday and sunday Date objects
 */
export const getWeekDateRange = (date) => {
  const monday = getMonday(date);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return { monday, sunday };
};

/**
 * Get the day name for a date
 * @param {Date|string} date - The date
 * @returns {string} Day name (e.g., "Monday")
 */
export const getDayName = (date) => {
  const d = _toLocalDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-IE', { weekday: 'long' });
};

/**
 * Get the number of days in a month
 * @param {number} year - The year
 * @param {number} month - The month (1-12)
 * @returns {number} Number of days in that month
 */
export const getDaysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

/**
 * Calculate the total cost of an event (packageTotal + roomHire + consumption)
 * Consumption is treated as F&B and always included.
 * @param {Object} event - The event object
 * @returns {number} Total cost of the event
 */
export const calculateEventTotal = (event) => {
  if (!event) return 0;
  return (event.packageTotal || 0) + (event.roomHire || 0) + (event.consumption || 0);
};

/**
 * Calculate the outstanding balance for an event
 * @param {Object} event - The event object
 * @param {Array} payments - Array of payment objects
 * @returns {number} Outstanding balance
 */
export const calculateBalance = (event, payments) => {
  if (!event) return 0;
  const totalCost = calculateEventTotal(event);

  const totalPaid = payments
    .filter((p) => p.event_id === event.id)
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  return totalCost - totalPaid;
};

/**
 * Calculate total revenue for an event (packageTotal + roomHire + consumption)
 * @param {Object} event - The event object
 * @returns {number} Total revenue for the event
 */
export const calculateEventRevenue = (event) => {
  if (!event) return 0;
  return calculateEventTotal(event);
};

/**
 * Get payments for an event grouped by category
 * @param {Object} event - The event object
 * @param {Array} payments - Array of payment objects
 * @returns {Object} Object with category totals and overall summary
 */
export const getPaymentsByCategory = (event, payments) => {
  if (!event) return { deposit: 0, room_hire: 0, fb_package: 0, final_balance: 0, other: 0, total: 0 };
  const evtPayments = payments.filter((p) => p.event_id === event.id);
  const byCategory = { deposit: 0, room_hire: 0, fb_package: 0, final_balance: 0, other: 0, package: 0 };
  evtPayments.forEach((p) => {
    const cat = p.category || 'package';
    byCategory[cat] = (byCategory[cat] || 0) + (p.amount || 0);
  });
  const total = evtPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  return { ...byCategory, total };
};

/**
 * Get payment status badge info
 * @param {Object} event - The event object
 * @param {Array} payments - Array of payment objects
 * @returns {Object} Object with label and color properties
 */
export const getPaymentStatusBadge = (event, payments) => {
  const totalCost = calculateEventTotal(event);
  if (!event || totalCost === 0) {
    return { label: 'No Quote', color: '#94A3B8' };
  }

  const totalPaid = payments
    .filter((p) => p.event_id === event.id)
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const outstanding = totalCost - totalPaid;

  if (outstanding <= 0) {
    return { label: 'Paid in Full', color: '#10B981' };
  }

  if (totalPaid === 0) {
    return { label: 'Unpaid', color: '#EF4444' };
  }

  if (totalPaid < totalCost * 0.5) {
    return { label: 'Deposit Only', color: '#F59E0B' };
  }

  return { label: 'Part Paid', color: '#F59E0B' };
};

/** Payment category labels for display */
export const PAYMENT_CATEGORIES = [
  { value: 'deposit', label: 'Deposit' },
  { value: 'room_hire', label: 'Room Hire' },
  { value: 'fb_package', label: 'F&B / Package' },
  { value: 'final_balance', label: 'Final Balance' },
  { value: 'other', label: 'Other' },
];

/** Get display label for a payment category */
export const getCategoryLabel = (category) => {
  const cat = PAYMENT_CATEGORIES.find((c) => c.value === category);
  return cat ? cat.label : category?.replace(/_/g, ' ') || 'Payment';
};

/** Shared deposit constants (used by Tables and Dashboard) */
export const DEPOSIT_AMOUNT = 25;
export const DEPOSIT_THRESHOLD = 5;

/**
 * Format a date range as short string
 * If endDate is null or same as start, returns just the start date.
 * @param {Date|string} startDate - The start date
 * @param {Date|string|null} endDate - The end date (optional)
 * @returns {string} Formatted date range (e.g., "Wed, 8 Apr – Fri, 10 Apr")
 */
export const fmtDateRange = (startDate, endDate) => {
  const start = fmtDate(startDate);
  if (!endDate) return start;
  const _key = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    if (v instanceof Date) {
      const y = v.getFullYear();
      const m = String(v.getMonth() + 1).padStart(2, '0');
      const d = String(v.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return '';
  };
  const s = _key(startDate);
  const e = _key(endDate);
  if (!e || s === e) return start;
  return `${start} – ${fmtDate(endDate)}`;
};

/**
 * Check if an event spans a given date (handles multi-day events)
 * @param {Object} event - The event object (with date and optional endDate)
 * @param {string} dateStr - The date to check (YYYY-MM-DD)
 * @returns {boolean} True if the event is active on that date
 */
export const eventSpansDate = (event, dateStr) => {
  if (!event || !event.date || !dateStr) return false;
  const end = event.endDate || event.date;
  return dateStr >= event.date && dateStr <= end;
};

/**
 * Get the number of days an event spans
 * @param {Object} event - The event object
 * @returns {number} Number of days (1 for single-day events)
 */
export const getEventDayCount = (event) => {
  if (!event || !event.date) return 1;
  const end = event.endDate || event.date;
  if (end === event.date) return 1;
  const startMs = new Date(event.date + 'T00:00:00').getTime();
  const endMs = new Date(end + 'T00:00:00').getTime();
  return Math.max(1, Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1);
};

/**
 * Check for venue conflicts in the calendar
 * Considers combinable spaces (partitions)
 * @param {string} venueId - The venue ID to check
 * @param {string} date - The date to check (YYYY-MM-DD format)
 * @param {Array} events - Array of all events
 * @param {Array} combinations - Array of venue combination rules
 * @returns {Array} Array of conflicting event objects
 */
export const checkVenueConflict = (venueId, date, events, combinations) => {
  if (!venueId || !date || !events) return [];

  // Get all venues that would be blocked if this venue is booked
  const blockedVenues = new Set([venueId]);

  // Check if this venue is a child partition (blocks parent)
  combinations.forEach((combo) => {
    if (combo.child_id === venueId && combo.type === 'partition') {
      blockedVenues.add(combo.parent_id);
    }
    // Check if this venue is a parent (blocks children)
    if (combo.parent_id === venueId && combo.type === 'partition') {
      blockedVenues.add(combo.child_id);
    }
  });

  // Find events that overlap this date and use blocked venues (supports multi-day)
  return events.filter(
    (evt) => {
      const evtEnd = evt.endDate || evt.date;
      // Check if the event's date range overlaps the target date
      return evt.date <= date && evtEnd >= date && blockedVenues.has(evt.venue_id);
    }
  );
};
